import React, { FormEvent, useState } from 'react';
import { Eye, EyeOff, User, Lock, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useAppState } from '../../context/AppStateContext';
import KoMoldLogo from '../brand/KoMoldLogo';
import LoginBackdrop from './LoginBackdrop';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';

interface LoginPageProps {
  onLoginSuccess: (token: string) => void;
}

/** One place for the field chrome, so the two inputs cannot drift apart. */
const FIELD_CLASS =
  'h-[52px] w-full rounded-xl border border-[#E5E7EB] bg-[#FDFDFD] pl-11 text-[15px] text-[#0F1317] ' +
  'transition-[border-color,box-shadow] duration-150 ease-out placeholder:text-[#9CA3AF] ' +
  'focus:border-[#3A6FC0] focus:outline-none focus:ring-4 focus:ring-[#3A6FC0]/18 ' +
  'disabled:cursor-not-allowed disabled:bg-[#F3F5F8]';

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const { login } = useAuth();
  const { appName } = useAppState();
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResetOpen, setIsResetOpen] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const token = await login(username, password, rememberMe);
      onLoginSuccess(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'เกิดข้อผิดพลาดที่ไม่รู้จัก');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-dvh flex-col bg-[#FAFBFC] font-noto-serif-thai">
      <LoginBackdrop />

      {/* Corner mark. The brand itself now sits above the card, so only the
          maker's line stays out here. */}
      <header className="relative z-10 flex items-center justify-end px-6 pt-6 sm:px-8 sm:pt-7">
        <span className="text-[11px] tracking-[0.02em] text-[#9CA3AF]">by DAICRAFT CO.</span>
      </header>

      <main className="relative z-10 flex flex-1 items-center justify-center px-5 py-10 sm:py-16">
        {/* Fades up once, in 180ms. */}
        <div className="w-full max-w-[420px] animate-card-in motion-reduce:animate-none">
          {/* The splash's logo flies onto this one and lands on it exactly, so
              the intro appears to hand the mark over rather than replace it.
              data-komold-anchor is what SplashScreen measures — keep it. */}
          <div className="mb-6 flex flex-col items-center gap-2">
            <span data-komold-anchor className="inline-flex">
              <KoMoldLogo className="h-[56px] w-[56px]" title="KoMold" />
            </span>
            <span className="text-[15px] font-semibold tracking-[-0.01em] text-[#0F1317]">
              KoMold
            </span>
          </div>
          <div className="rounded-[24px] border border-[#E5E7EB] bg-[#FDFDFD] p-8 shadow-[0_12px_40px_rgba(42,75,135,0.10)] sm:p-10">
            <div className="mb-7">
              <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.02em] text-[#0F1317]">
                เข้าสู่ระบบ
              </h1>
              <p className="mt-1.5 text-[14px] leading-relaxed text-[#6B7280]">
                {appName} · ระบบจัดการแม่พิมพ์
              </p>
            </div>

            {error && (
              <div
                role="alert"
                className="mb-5 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-3.5 py-3"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 flex-none text-red-500" />
                <span className="text-[13.5px] leading-snug text-red-700">{error}</span>
              </div>
            )}

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="login-username" className="mb-2 block text-[13px] font-medium text-[#374151]">
                  รหัสผู้ใช้
                </label>
                <div className="relative">
                  <User className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-[#9CA3AF]" />
                  <input
                    id="login-username"
                    type="text"
                    autoComplete="username"
                    className={`${FIELD_CLASS} pr-4`}
                    placeholder="กรอกรหัสผู้ใช้"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={isLoading}
                    required
                    data-cy="username-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="login-password" className="mb-2 block text-[13px] font-medium text-[#374151]">
                  รหัสผ่าน
                </label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-[#9CA3AF]" />
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className={`${FIELD_CLASS} pr-12`}
                    placeholder="กรอกรหัสผ่าน"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    required
                    data-cy="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-[#9CA3AF] transition-colors duration-150 hover:bg-[#F0F2F6] hover:text-[#0F1317]"
                    disabled={isLoading}
                    aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                  >
                    {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 pt-0.5">
                <label htmlFor="login-remember" className="flex cursor-pointer items-center gap-2.5 select-none">
                  <input
                    id="login-remember"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isLoading}
                    className="h-4 w-4 cursor-pointer rounded border-[#C9D0DA] accent-[#3A6FC0] transition-colors duration-150 focus:ring-2 focus:ring-[#3A6FC0]/25 focus:ring-offset-0"
                  />
                  <span className="text-[13px] text-[#6B7280]">จดจำการเข้าสู่ระบบ</span>
                </label>

                <button
                  type="button"
                  onClick={() => setIsResetOpen(true)}
                  className="rounded text-[13px] font-medium text-[#3A6FC0] transition-colors duration-150 hover:text-[#2B559A] focus:outline-none focus:ring-2 focus:ring-[#3A6FC0]/30"
                >
                  ลืมรหัสผ่าน?
                </button>
              </div>

              <button
                type="submit"
                className="flex h-[52px] w-full items-center justify-center gap-2 rounded-[14px] bg-[#0F1317] text-[15px] font-semibold text-white shadow-[0_1px_2px_rgba(42,75,135,0.14)] transition-[transform,box-shadow,background-color] duration-150 ease-out hover:-translate-y-px hover:bg-[#1B2128] hover:shadow-[0_6px_16px_rgba(42,75,135,0.22)] focus:outline-none focus:ring-4 focus:ring-[#0F1317]/15 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-[0_1px_2px_rgba(42,75,135,0.14)] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                disabled={isLoading}
              >
                {isLoading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {isLoading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
              </button>
            </form>
          </div>
        </div>
      </main>

      <footer className="relative z-10 px-6 pb-6 text-center text-[11.5px] text-[#9CA3AF] sm:px-8 sm:pb-7">
        © {new Date().getFullYear()} DAICRAFT CO., LTD.
      </footer>

      {/* There is no self-service reset: an administrator sets the password. The
          dialog says so plainly rather than sending the person to a dead link. */}
      <Dialog open={isResetOpen} onOpenChange={setIsResetOpen}>
        <DialogContent className="max-w-[400px] rounded-[20px] font-noto-serif-thai">
          <DialogHeader>
            <DialogTitle className="text-[17px] font-semibold text-[#0F1317]">
              ลืมรหัสผ่าน?
            </DialogTitle>
            <DialogDescription className="pt-1 text-[14px] leading-relaxed text-[#6B7280]">
              กรุณาติดต่อผู้ดูแลระบบของบริษัทเพื่อขอตั้งรหัสผ่านใหม่
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              className="h-11 w-full rounded-xl bg-[#0F1317] text-[14px] font-semibold hover:bg-[#1B2128]"
              onClick={() => setIsResetOpen(false)}
            >
              เข้าใจแล้ว
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
